/* eslint-disable no-console */
/**
 * Main entry point
 * - Load required dependencies (react, redux, react-loadable, loader)
 * - Load asynchronously the application (store, router)
 * - Init the Enzyme Api before rendering the application
 * - Render the application with CSP nonce support
 */
import 'react-app-polyfill/ie9'
import 'react-app-polyfill/ie11'
import 'core-js/stable'
import 'react-app-polyfill/stable'

import React from 'react'
import ReactDOM from 'react-dom'
import Loadable from 'react-loadable'
import { Provider } from 'react-redux'
import { Helmet } from 'react-helmet'
import ReactGA from 'react-ga'
import i18n from 'i18next'
import Moment from 'moment'
import localizationFr from 'moment/locale/fr'

import messagesfr from '@translations/fr.json'
import messagesen from '@translations/en.json'
import ErrorBoundary from '@src/components/views/ErrorView'
import ReduxEnzyme from '@libs/redux-enzyme'
import Loader from '@CommonUi/Loader'
import Store, { apiOptions, appViews } from '@database'
import CacheBuster from './CacheBuster'
import reportWebVitals from './reportWebVitals'

// Désactiver les logs en production
if (process.env.NODE_ENV === 'production') {
  console.log = () => {}
  console.debug = () => {}
  console.info = () => {}
}

/**
 * Configuration i18n
 */
i18n.init({
  lng: 'fr',
  debug: process.env.NODE_ENV === 'development',
  resources: {
    en: { translation: messagesen },
    fr: { translation: messagesfr }
  }
})

Moment.locale('fr', localizationFr)

/**
 * Configuration Google Analytics avec CSP nonce
 */
const initializeAnalytics = () => {
  // Récupérer le nonce CSP depuis une meta tag
  const nonceMetaTag = document.querySelector('meta[name="csp-nonce"]')
  const nonce = nonceMetaTag ? nonceMetaTag.getAttribute('content') : null

  if (nonce) {
    // Initialiser GA avec le nonce pour respecter CSP
    window.ga_debug = { trace: process.env.NODE_ENV === 'development' }

    ReactGA.initialize('UA-80262003-1', {
      gaOptions: {
        siteSpeedSampleRate: 100
      }
    })
    ReactGA.pageview(window.location.pathname + window.location.search)
  } else {
    console.warn('CSP nonce not found - Google Analytics may be blocked by CSP')
  }
}

// Initialiser GA après le chargement du DOM
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeAnalytics)
} else {
  initializeAnalytics()
}

/**
 * Router avec lazy loading
 */
const Router = Loadable({
  loader: () => import(/* webpackChunkName: "router" */ '@routes'),
  loading: Loader,
  delay: 60,
  timeout: 10000
})

/**
 * Init redux-enzyme Library
 */
ReduxEnzyme.init({
  options: apiOptions,
  store: Store
})

/**
 * Configuration de l'application
 */
const companyName = process.env.FRONT_COMPANY_NAME || 'Mentoring Platform'
const companySlogan =
  process.env.FRONT_COMPANY_SLOGAN ||
  'Peer Learning Solution pour Entreprises Apprenantes.'

/**
 * Composant principal de l'application
 */
const App = () => (
  <Provider store={Store}>
    <ErrorBoundary>
      <Helmet>
        <title>
          {companyName}, {companySlogan}
        </title>
        <meta name='description' content={companySlogan} />
      </Helmet>
      <CacheBuster>
        {({ loading, isLatestVersion, refreshCacheAndReload }) => {
          if (loading) return <Loader />

          if (!loading && !isLatestVersion) {
            // Rafraîchir le cache uniquement si nécessaire
            refreshCacheAndReload()
            return null
          }

          return <Router views={appViews} />
        }}
      </CacheBuster>
    </ErrorBoundary>
  </Provider>
)

/**
 * Rendu de l'application
 */
const rootElement = document.getElementById('root')

if (!rootElement) {
  throw new Error(
    'Root element not found. Make sure you have <div id="root"></div> in your HTML.'
  )
}

ReactDOM.render(<App />, rootElement)

/**
 * Web Vitals reporting
 */
reportWebVitals(metric => {
  // Envoyer les métriques à Google Analytics si configuré
  if (window.gtag) {
    window.gtag('event', metric.name, {
      event_category: 'Web Vitals',
      value: Math.round(
        metric.name === 'CLS' ? metric.value * 1000 : metric.value
      ),
      event_label: metric.id,
      non_interaction: true
    })
  }

  // Log en développement
  if (process.env.NODE_ENV === 'development') {
    console.log(metric)
  }
})

/**
 * Hot Module Replacement pour le développement
 */
if (process.env.NODE_ENV === 'development' && module.hot) {
  module.hot.accept('@routes', () => {
    const NextRouter = require('@routes').default
    ReactDOM.render(
      <Provider store={Store}>
        <ErrorBoundary>
          <NextRouter views={appViews} />
        </ErrorBoundary>
      </Provider>,
      rootElement
    )
  })
}

/**
 * Debug info en développement
 */
if (process.env.NODE_ENV === 'development') {
  console.group('🔧 Environment Configuration')
  console.log('NODE_ENV:', process.env.NODE_ENV)
  console.log('APP_ENV:', process.env.APP_ENV)
  console.log('API_BASE_URL:', process.env.API_BASE_URL)
  console.log('FRONT_DOMAIN_NAME:', process.env.FRONT_DOMAIN_NAME)
  console.log('VERSION:', process.env.VERSION)
  console.groupEnd()
}
